import { Link } from 'react-router-dom'
import type { PatientTreatment } from '../../store/patientDetailSlice'

interface TreatmentHistoryTabProps {
  treatments: PatientTreatment[]
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr.split('T')[0] + 'T00:00:00')
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Completed':
      return 'bg-accent-subtle-bg text-status-success'
    case 'In Progress':
      return 'bg-[#dae8f0] text-status-info'
    case 'Planned':
      return 'bg-[#f0e8d0] text-status-warning'
    case 'Cancelled':
      return 'bg-border text-text-muted'
    default:
      return 'bg-border text-text-muted'
  }
}

export function TreatmentHistoryTab({ treatments }: TreatmentHistoryTabProps) {
  return (
    <div data-testid="treatment-history-tab">
      {treatments.length === 0 ? (
        <p className="text-[13px] text-text-muted py-4" data-testid="no-treatment-history">
          No treatment history
        </p>
      ) : (
        <div className="grid grid-cols-1" style={{ gridTemplateRows: '36px repeat(auto-fill, 44px)' }}>
          <div className="th-table-header grid items-center h-9 text-[12px] font-medium text-text-muted" style={{ gridTemplateColumns: '100px 1fr 1fr 1fr 110px' }}>
            <span className="px-2">Date</span>
            <span className="px-2">Type</span>
            <span className="px-2">Diagnosis</span>
            <span className="px-2">Veterinarian</span>
            <span className="px-2">Status</span>
          </div>
          {treatments.map((t) => {
            const txId = `TX-${String(t.treatment_number).padStart(4, '0')}`
            return (
              <Link
                key={t.id}
                to={`/treatments/${t.id}`}
                data-testid="treatment-history-item"
                className="th-table-row grid items-center h-11 text-[13px] text-text-primary hover:bg-hover rounded-[4px] no-underline cursor-pointer"
                style={{ gridTemplateColumns: '100px 1fr 1fr 1fr 110px' }}
              >
                <span className="px-2 text-text-muted" data-testid="treatment-date">{formatDate(t.treatment_date)}</span>
                <span className="px-2" data-testid="treatment-type">
                  <span className="text-text-muted font-[450] mr-2">{txId}</span>
                  {t.treatment_type}
                </span>
                <span className="px-2 text-text-muted truncate" data-testid="treatment-diagnosis">{t.diagnosis || '—'}</span>
                <span className="px-2 text-text-muted" data-testid="treatment-vet">{t.veterinarian_name || '—'}</span>
                <span className="px-2">
                  <span
                    data-testid="treatment-status-badge"
                    className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(t.status)}`}
                  >
                    {t.status}
                  </span>
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
